import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Brain, Sparkles, MessageSquare, Phone, Mail, MapPin, Wallet, Clock,
  Target, Activity, Send, RefreshCw, UserCheck, PlayCircle, PauseCircle,
} from "lucide-react";
import { API, apiError } from "../lib/api";
import { Spinner } from "../components/common";
import { tempColor, scoreColor, stageBadge, stageLabel, typeBadge, timeAgo, formatINR } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";

const INVESTOR_STAGES = ["NEW", "CONTACTED", "ENGAGED", "QUALIFYING", "QUALIFIED", "NURTURING", "HOT", "ADMIN_REVIEW", "ASSIGNED", "MEETING", "SITE_VISIT", "NEGOTIATION", "BOOKING", "CLOSED_WON", "CLOSED_LOST", "DO_NOT_CONTACT"];
const ASSOCIATE_STAGES = ["NEW", "CONTACTED", "INTERESTED", "QUALIFIED", "TRAINING", "ACTIVE", "PERFORMING", "INACTIVE"];

export default function LeadProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [lead, setLead] = useState(null);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const load = () => {
    API.get(`/leads/${id}`).then((r) => setLead(r.data));
    API.get(`/leads/${id}/events`).then((r) => setEvents(r.data));
    API.get(`/leads/${id}/notes`).then((r) => setNotes(r.data));
  };
  useEffect(load, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try { const { data } = await API.post(`/leads/${id}/analyze`); setLead(data); toast.success("AI re-analyzed the lead"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  const setStage = async (stage) => {
    const { data } = await API.put(`/leads/${id}`, { stage });
    setLead(data); load(); toast.success(`Stage → ${stageLabel(stage)}`);
  };

  const reviewAction = async (changes, message) => {
    try { const {data}=await API.put(`/leads/${id}`,changes);setLead(data);toast.success(message);load(); }
    catch(e){toast.error(apiError(e));}
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await API.post(`/leads/${id}/notes`, { text: note });
    setNote(""); load(); toast.success("Note added");
  };

  if (!lead) return <Spinner />;
  const stages = lead.lead_type === "ASSOCIATE" ? ASSOCIATE_STAGES : INVESTOR_STAGES;

  const Field = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-slate-500 mt-0.5" />
      <div><div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-sm text-slate-200">{value || "—"}</div></div>
    </div>
  );

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1.5 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: identity + fields */}
        <div className="lg:col-span-1 space-y-6">
          <div className="mb-card p-6" data-testid="lead-identity">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-full flex items-center justify-center text-lg font-bold ${lead.lead_type === "ASSOCIATE" ? "bg-emerald-500/15 text-emerald-400" : "bg-blue-500/15 text-blue-400"}`}>
                {lead.name.charAt(0)}
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-100">{lead.name}</h1>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadge(lead.lead_type)}`}>{lead.lead_type}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 mt-5">
              <Field icon={Phone} label="Phone" value={lead.phone} />
              <Field icon={Mail} label="Email" value={lead.email} />
              <Field icon={MapPin} label="Location" value={lead.preferred_location} />
              <Field icon={Wallet} label="Budget" value={lead.budget} />
              <Field icon={Clock} label="Timeline" value={lead.timeline} />
              <Field icon={Target} label="Purpose" value={lead.purpose} />
              <Field icon={Activity} label="Source / Campaign" value={`${lead.source}${lead.campaign ? " · " + lead.campaign : ""}`} />
            </div>
            <div className="mt-5 pt-4 border-t border-slate-800 flex gap-2">
              <button onClick={() => nav(`/whatsapp?lead=${lead.id}`)} className="flex-1 btn-secondary bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 rounded-lg py-2 text-sm flex items-center justify-center gap-1.5" data-testid="open-whatsapp-btn">
                <MessageSquare className="h-4 w-4" /> WhatsApp
              </button>
            </div>
          </div>

          {/* AI Memory */}
          <div className="mb-card p-5" data-testid="ai-memory">
            <div className="caption-mono mb-3">AI Memory</div>
            <div className="text-xs space-y-1.5 text-slate-300">
              <div><span className="text-slate-500">Occupation:</span> {lead.occupation || "—"}</div>
              <div><span className="text-slate-500">Experience:</span> {lead.investment_experience || "—"}</div>
              <div><span className="text-slate-500">Project interest:</span> {lead.project_interest || "—"}</div>
              <div><span className="text-slate-500">Objections:</span> {(lead.memory?.objections || []).join(", ") || "none noted"}</div>
              <div><span className="text-slate-500">Sources recorded:</span> {lead.sources?.length || 1}</div>
            </div>
          </div>
        </div>

        {/* Middle+Right: AI + stage + timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI intelligence */}
          <div className="mb-card p-6 border-purple-500/20" data-testid="ai-intelligence-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-purple-500/15 flex items-center justify-center"><Brain className="h-4 w-4 text-purple-400" /></div>
                <span className="font-heading font-semibold text-slate-100">AI Intelligence</span>
              </div>
              <button onClick={analyze} disabled={analyzing} className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1.5" data-testid="reanalyze-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${analyzing ? "animate-spin" : ""}`} /> Re-analyze
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div><div className="caption-mono">Score</div><div className={`font-heading text-3xl font-bold ${scoreColor(lead.score)}`}>{lead.score}</div></div>
              <div><div className="caption-mono">Temp</div><div><span className={`text-xs px-2 py-1 rounded-full border capitalize ${tempColor(lead.temperature)}`}>{lead.temperature}</span></div></div>
              <div><div className="caption-mono">Intent</div><div className="text-lg font-semibold text-slate-200 capitalize">{lead.intent}</div></div>
              <div><div className="caption-mono">Confidence</div><div className="text-lg font-semibold text-slate-200">{lead.confidence}%</div></div>
            </div>
            <div className="text-sm text-slate-300 bg-slate-900/50 border border-slate-800 rounded-lg p-3 mb-2">{lead.ai_summary || "No summary yet."}</div>
            {lead.ai_recommended_action && (
              <div className="text-sm text-amber-300 flex items-start gap-2"><Sparkles className="h-4 w-4 mt-0.5" /> {lead.ai_recommended_action}</div>
            )}
          </div>

          {/* Stage pipeline */}
          <div className="mb-card p-5" data-testid="stage-pipeline">
            <div className="caption-mono mb-3">Pipeline Stage — {stageLabel(lead.stage)}</div>
            <div className="flex flex-wrap gap-2">
              {stages.map((s) => (
                <button key={s} onClick={() => setStage(s)} data-testid={`stage-${s}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${s === lead.stage ? stageBadge(s) + " ring-1 ring-amber-500/40" : "border-slate-800 text-slate-500 hover:text-slate-300 hover:border-slate-600"}`}>
                  {stageLabel(s).toLowerCase()}
                </button>
              ))}
            </div>
            {isAdmin && lead.stage === "ADMIN_REVIEW" && <div className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-500/5"><div className="text-sm font-semibold text-amber-300 mb-2">Admin decision required</div><div className="text-xs text-slate-400 mb-3">Review the conversation, then approve for assignment, continue AI qualification, or pause automation.</div><div className="flex flex-wrap gap-2"><button onClick={()=>{reviewAction({stage:"HOT",needs_human:true},"Approved for assignment");nav("/hot-leads");}} className="btn-gold px-3 py-2 text-xs flex gap-1"><UserCheck className="h-4 w-4"/>Approve & assign</button><button onClick={()=>reviewAction({stage:"QUALIFIED",needs_human:false,ai_mode:true},"AI qualification resumed")} className="px-3 py-2 text-xs rounded-lg border border-emerald-500/30 text-emerald-300 flex gap-1"><PlayCircle className="h-4 w-4"/>Continue AI</button><button onClick={()=>reviewAction({ai_mode:false},"AI paused for human handling")} className="px-3 py-2 text-xs rounded-lg border border-slate-700 text-slate-300 flex gap-1"><PauseCircle className="h-4 w-4"/>Pause AI</button></div></div>}
          </div>

          {/* Notes */}
          <div className="mb-card p-5" data-testid="notes-panel">
            <div className="caption-mono mb-3">Notes</div>
            <div className="flex gap-2 mb-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="note-input"
                className="flex-1 bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
              <button onClick={addNote} className="btn-gold px-3 py-2" data-testid="add-note-btn"><Send className="h-4 w-4" /></button>
            </div>
            <div className="space-y-2">
              {notes.map((n) => (
                <div key={n.id} className="text-sm text-slate-300 bg-slate-900/40 border border-slate-800 rounded-lg p-2.5">
                  {n.text}<div className="text-[10px] text-slate-500 mt-1">{n.author} · {timeAgo(n.created_at)}</div>
                </div>
              ))}
              {notes.length === 0 && <div className="text-xs text-slate-500">No notes yet.</div>}
            </div>
          </div>

          {/* Timeline */}
          <div className="mb-card p-5" data-testid="lead-timeline">
            <div className="caption-mono mb-4">Activity Timeline · Audit Trail</div>
            <div className="space-y-4">
              {events.map((e) => (
                <div key={e.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="h-2 w-2 rounded-full bg-amber-500 mt-1.5" />
                    <div className="flex-1 w-px bg-slate-800 my-1" />
                  </div>
                  <div className="pb-1">
                    <div className="text-xs font-mono text-amber-400/80">{e.type}</div>
                    <div className="text-sm text-slate-300">{e.message}</div>
                    <div className="text-[10px] text-slate-500">{timeAgo(e.created_at)}</div>
                  </div>
                </div>
              ))}
              {events.length === 0 && <div className="text-xs text-slate-500">No activity yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
